// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Company listing index route module.
 * Displays all companies by default (currentView='all').
 *
 * This route renders the company list with no role filtering,
 * showing all companies regardless of whether they are customers or suppliers.
 */
import { PageContent } from '@/components/page-content';
import { crmSyncStatusSchema } from '@/constants/crm-sync-status';
import { sortDirectionSchema } from '@/constants/sort';
import { CustomerFilter } from '@/customer/api/get-customers';
import { CompanyList } from '@/customer/components/company-list';
import { customerSortPropertySchema } from '@/customer/hooks/use-customers';
import { searchTermSchema } from '@/utilities/search-term';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * Schema for validating URL search parameters.
 * Ensures type safety for route parameters.
 */
const searchSchema = z.object({
  query: searchTermSchema,
  archived: z.boolean().optional(),
  syncStatuses: z.array(crmSyncStatusSchema).optional(),
  direction: sortDirectionSchema.optional(),
  sort: customerSortPropertySchema.optional(),
  page: z.number().optional(),
  new: z.string().optional(),
});

/**
 * Company listing route configuration.
 * Sets up the component and search parameter validation.
 */
export const Route = createFileRoute('/_app/companies/')({
  component: CompaniesIndexRoute,
  validateSearch: searchSchema,
});

/**
 * Company listing index route component.
 * Displays all companies without role filtering.
 */
function CompaniesIndexRoute() {
  const search = Route.useSearch();

  /**
   * Filter configuration for showing all companies.
   * No role filtering applied (isCustomer and isSupplier are undefined).
   */
  const filter: CustomerFilter = {
    isArchived: search.archived ? 'all' : undefined,
    query: search.query,
    syncStatuses: search.syncStatuses,
  };

  return (
    <PageContent>
      <CompanyList currentView="all" filter={filter} search={search} />
    </PageContent>
  );
}
